import { FiX } from "react-icons/fi";

type MultiSelectClearButtonProps = {
  onClear: () => void;
  disabled?: boolean;
};

/**
 * Kendo's own MultiSelect clear-all ("x") button is hard-coded with
 * tabIndex={-1} (see the .k-clear-value rule in kendo-overrides.css that
 * hides it), so it's never reachable by Tab - only by mouse, or by an
 * undocumented Escape-to-clear shortcut. This is a real, focusable
 * replacement rendered via the `suffix` prop alongside
 * MultiSelectToggleButton - only render it once there's something to clear,
 * matching Kendo's own visibility rule for its built-in button.
 * Pass `disabled` whenever the MultiSelect itself is disabled/read-only:
 * Kendo's own `disabled` prop has no effect on elements rendered through
 * `suffix` (they're ours, not Kendo's), so without this a "disabled" or
 * view-only field could still have its selections cleared via keyboard.
 */
export function MultiSelectClearButton({
  onClear,
  disabled,
}: MultiSelectClearButtonProps) {
  return (
    <button
      type="button"
      className="multiselect-clear-button"
      aria-label="Clear all selected items"
      disabled={disabled}
      onMouseDown={(e) => {
        // Same reasoning as MultiSelectToggleButton: prevent Kendo's own
        // mousedown-triggered blur/close, and let onClick be the single
        // source of the actual clear action (mouse or keyboard alike).
        e.preventDefault();
      }}
      onClick={onClear}
    >
      <FiX aria-hidden="true" />
    </button>
  );
}
